import {VNode} from 'snabbdom/vnode';
import {Component, jsx} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {LabelTheme, SectionItem} from 'common/components/items-menu/types';
import {ChartLegendEl} from 'common/components/chart-legend';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';
import {otherSegment} from 'common/util/colors';

interface SegmentLegendHeaderState {
  width: number; // in px
}

function getMaxLegendItemsForOneRow(availableWidth: number): number {
  if (availableWidth < 256) {
    return 2;
  }
  if (availableWidth < 512) {
    return 3;
  }
  if (availableWidth < 768) {
    return 5;
  }
  return 6;
}

// single row legend header
export class SegmentLegendHeader extends Component<
  SegmentLegendHeaderState,
  EmptyObj,
  any,
  any,
  ChartsContextRegistry
> {
  get config() {
    return {
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {
        width: 0,
      },
      contexts: [`segmentFilterController` as const],
    };
  }

  private selfResizeObserver: ResizeObserver;
  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  connectedCallback() {
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {width} = entries[0].contentRect;
      this.update({width});
    });
    this.selfResizeObserver.observe(this);

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    super.disconnectedCallback();
  }

  private renderTemplate(): VNode {
    // for nested data, we only care about the first section
    const visibleSegmentItems = this.segmentFilterController.getSelectedItems(0);
    const legendItems: Array<SectionItem<string>> = visibleSegmentItems.map((segmentItem) => {
      // special case to show different color in header vs segment-filter
      const color =
        segmentItem.id === OTHER_SEGMENT_VALUE
          ? otherSegment.primary
          : this.segmentFilterController.getItemColor(0, segmentItem.id);

      return {
        label: segmentItem.label,
        value: segmentItem.id,
        isSelected: true,
        labelTheme: LabelTheme.Soft,
        wrapLabel: true,
        color,
      };
    });

    const maxLegendItems = getMaxLegendItemsForOneRow(this.state.width);
    return (
      <ChartLegendEl
        attrs={{
          loading: !this.segmentFilterController.hasLoaded(),
          theme: ChartLegendTheme.CardTable,
          'max-legend-items': maxLegendItems,
          'max-table-item-width': `${Math.floor(100 / Math.min(maxLegendItems, legendItems.length || 1))}%`,
        }}
        props={{
          sections: [{items: legendItems}],
        }}
      />
    );
  }
}

export const SegmentLegendHeaderEl = defineAndMakeJsxFactory(`mp-segment-legend-header`, SegmentLegendHeader);
